import { useEffect, useRef } from 'react';
import useLiveSessionStore from '@/channels/liveSession/liveSessionStore';
import { logLiveSessionEvent } from '@/modules/liveSessionEvents';

interface SyncableSlide {
  id: number | string;
  position?: number;
  type?: string;
}

const BROADCAST_DEBOUNCE_MS = 400;

const isValidSlideTarget = (slideId: number, slideType: string): boolean => (
  Number.isFinite(slideId) && slideId >= 0 && (slideId !== 0 || slideType === 'Transition')
);

// AIDEV-NOTE: PLAT-17352 — debounced (400ms trailing) so holding an arrow key only
// broadcasts the final slide, not every intermediate.
const useTeacherSlideSync = (
  selectedSlide: SyncableSlide | null,
  userType: string,
) => {
  const performNavigate = useLiveSessionStore(state => state.performNavigate);
  const sessionId = useLiveSessionStore(state => state.sessionId);
  const slideId = selectedSlide ? Number(selectedSlide.id) : NaN;
  const slideType = selectedSlide?.type === 'Transition' ? 'Transition' : 'Slide';
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (userType === 'Student') return;
    if (!sessionId) return;
    if (!selectedSlide) return;
    if (!isValidSlideTarget(slideId, slideType)) {
      logLiveSessionEvent(sessionId, 'teacher_nav_guarded', {
        reason: 'invalid_slide', slide_id: selectedSlide.id, slide_type: slideType,
      });
      return;
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const position = selectedSlide.position ?? null;
      performNavigate(slideId, slideType, position);
      // A send while disconnected is dropped by Action Cable with no error; the forensic log
      // pairs this with the server's navigate_to_slide to make that visible.
      logLiveSessionEvent(sessionId, 'teacher_nav_sent', {
        connection_state: useLiveSessionStore.getState().connectionState,
        position,
        slide_id: slideId,
        slide_type: slideType,
      });
    }, BROADCAST_DEBOUNCE_MS);

    return () => { clearTimeout(timerRef.current); };
  // Primitive deps avoid re-fires when selectedSlide is a new reference but (id, type) match.
  }, [slideId, slideType, sessionId, userType, performNavigate]);
};

export default useTeacherSlideSync;
